import { Button, Classes, Intent, TextArea } from '@blueprintjs/core';
import * as React from 'react';
import { Dialog } from 'vet-bones/components';

import { TEST_IDS } from 'src/constants/testIds';
import { useProjectModelSettings } from 'src/containers/ProjectSettingsDrawer/hooks/useProjectModelSettingsValue';
import { projectModelsByIdSelector } from 'src/selectors/projectModelsById';
import * as copy from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';
import { useRouterParams } from 'src/utils/hooks/useRouterParams';

import styles from 'src/containers/ProjectSettingsDrawer/projectSettingsDrawerAiRulesDialog.module.scss';

type ProjectSettingsDrawerAiRulesDialogProps = {
  aiRulesModelId: string;
  onClose: () => void;
};

const aiHintsCopy = copy.components.projectSettingsDrawer.aiHints;

export const ProjectSettingsDrawerAiRulesDialog: React.VFC<
  ProjectSettingsDrawerAiRulesDialogProps
> = ({ aiRulesModelId, onClose }) => {
  const { projectId } = useRouterParams();
  const modelsById = useReduxSelector(projectModelsByIdSelector(projectId));

  const { voiceboxInstructionsByModel, setVoiceboxInstructionsByModel } =
    useProjectModelSettings();

  const initialValue = voiceboxInstructionsByModel[aiRulesModelId] || '';

  const [value, setValue] = React.useState(initialValue);

  React.useEffect(() => {
    if (aiRulesModelId) {
      setValue(initialValue);
    }
  }, [aiRulesModelId, initialValue]);

  const onSave = React.useCallback(
    (value: string) => {
      if (aiRulesModelId) {
        setVoiceboxInstructionsByModel((prev) => ({
          ...prev,
          [aiRulesModelId]: value,
        }));
      }
      onClose();
    },
    [aiRulesModelId, onClose, setVoiceboxInstructionsByModel]
  );

  const modelName = modelsById[aiRulesModelId]?.name;

  return (
    <Dialog
      canEscapeKeyClose
      canOutsideClickClose={false}
      className={styles.dialog}
      closeIconTestId={TEST_IDS.dialog.closeIcon}
      data-testid={TEST_IDS.projectSettingsDrawer.aiRulesDialog}
      isOpen={Boolean(aiRulesModelId)}
      onClose={onClose}
      title={aiHintsCopy.dialogTitle(modelName)}
    >
      <div className={Classes.DIALOG_BODY}>
        <div className={styles.descriptionBold}>{aiHintsCopy.description}</div>
        <div className={styles.descriptionLight}>
          {aiHintsCopy.examplesText}
        </div>
        <TextArea
          data-testid={TEST_IDS.projectSettingsDrawer.aiRulesTextarea}
          fill
          growVertically={false}
          onChange={(e) => setValue(e.target.value)}
          placeholder={aiHintsCopy.placeholder}
          rows={10}
          value={value}
        />
      </div>
      <div className={Classes.DIALOG_FOOTER}>
        <div className={Classes.DIALOG_FOOTER_ACTIONS}>
          <Button
            data-testid={TEST_IDS.projectSettingsDrawer.aiRulesCancelButton}
            intent={Intent.PRIMARY}
            onClick={onClose}
            outlined
            text={aiHintsCopy.cancel}
          />
          <Button
            data-testid={TEST_IDS.projectSettingsDrawer.aiRulesSaveButton}
            intent={Intent.PRIMARY}
            onClick={() => onSave(value)}
            text={aiHintsCopy.save}
          />
        </div>
      </div>
    </Dialog>
  );
};
